import { type FC, type ReactNode } from "react";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import type { SxProps } from "@mui/system";
import type { Theme } from "@mui/material";
import {
  Box,
  Card,
  Chip,
  Divider,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import CurrencyFormat, { currencyFormatMap } from "../../utils/currency-format";
import { useTranslation } from "react-i18next";

interface PricingPlanProps {
  cta: string;
  description?: any;
  features: any[];
  icon?: ReactNode;
  name: string;
  popular?: boolean;
  price: string | number | null;
  yearlyPayment: any;
  disabled?: boolean;
  priceDescription: any;
  featuresTitle: string;
  chipLabel?: string;
  previousPrice?: string | number | null;
  bestPrice?: boolean;
  sx?: SxProps<Theme>;
}

export const PricingPlanExternal: FC<PricingPlanProps> = (props) => {
  const { t } = useTranslation();
  const {
    cta,
    description,
    icon,
    features,
    name,
    popular,
    price,
    disabled,
    yearlyPayment,
    priceDescription,
    featuresTitle,
    chipLabel,
    previousPrice,
    bestPrice,
    sx,
    ...other
  } = props;

  let subscriptionValue = Number(price);
  return (
    <>
      <Box
        sx={{
          backgroundColor: bestPrice ? "#29A776" : "inherit",
          p: bestPrice ? "0.5rem" : "4rem 0.5rem 0.5rem",
          borderRadius: 1,
          height: "100%",
        }}
      >
        {bestPrice && (
          <Typography
            sx={{
              color: "#FFF",
              textAlign: "center",
              fontWeight: 700,
              mb: 1,
              mt: 0.5,
            }}
          >
            {t("pricing_table__general__labels__best_price")}
          </Typography>
        )}
        <Card
          sx={{
            display: "flex",
            flexDirection: "column",
            border: !bestPrice ? "0.5px solid #f5f5f5" : "none",
            //width: "100%",
            ...sx,
          }}
          {...other}
        >
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: !previousPrice ? "3.2rem" : "innerit",
              }}
            >
              <Typography sx={{ mt: 2 }} variant="h6">
                {name}
              </Typography>
              {chipLabel && (
                <Chip
                  sx={{
                    color: "#FFF",
                    fontWeight: 700,
                    fontSize: 16,
                    "&.MuiChip-filledDefault": { backgroundColor: "#ffae00" },
                  }}
                  label={chipLabel}
                  size="medium"
                />
              )}
            </Box>
            <Box sx={{ display: "flex", textDecoration: "line-through" }}>
              <Typography variant="h5">
                {previousPrice !== null &&
                  CurrencyFormat(
                    Number(previousPrice).toString(),
                    currencyFormatMap["SE"]
                  )}
              </Typography>
              {previousPrice !== null && (
                <Typography
                  color="text.secondary"
                  sx={{
                    alignSelf: "flex-end",
                    ml: 1,
                  }}
                  variant="body2"
                >
                  {t("pricing_table__general__labels__plan_recurrency")}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex" }}>
              <Typography variant="h4">
                {price !== null
                  ? price === 0
                    ? t("pricing_table__general__labels__free_tier_value")
                    : CurrencyFormat(
                        Number(subscriptionValue).toString(),
                        currencyFormatMap["SE"]
                      )
                  : t("pricing_table__general__labels__comming_soon")}
              </Typography>
              {price !== null && price !== 0 && (
                <Typography
                  color="text.secondary"
                  sx={{
                    alignSelf: "flex-end",
                    ml: 1,
                  }}
                  variant="subtitle2"
                >
                  {t("pricing_table__general__labels__plan_recurrency")}
                </Typography>
              )}
            </Box>
            <Typography
              color="text.secondary"
              sx={{ mt: 0, mb: !priceDescription ? "2.2rem" : "innerit" }}
              variant="body2"
            >
              {price !== null ? priceDescription : " "}
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ mt: price !== null ? 2 : 5, fontWeight: 600 }}
              variant="body2"
            >
              {description}
            </Typography>
          </Box>
          <Divider />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              p: 3,
              minHeight: "32rem",
            }}
          >
            <Stack spacing={2} sx={{ flexGrow: 1 }}>
              <Stack
                alignItems="center"
                direction="row"
                spacing={1}
                sx={{
                  alignItems: "center",
                  display: "flex",
                }}
              >
                <Typography sx={{ fontWeight: 700 }} variant="body2">
                  {featuresTitle}
                </Typography>
              </Stack>
              {features.map((feature, i) => (
                <Stack
                  alignItems="center"
                  direction="row"
                  spacing={1}
                  key={`${feature.describe}-${i}`}
                  sx={{
                    alignItems: "center",
                    display: "flex",
                  }}
                >
                  {feature?.include ? (
                    <SvgIcon color="success">
                      <CheckIcon />
                    </SvgIcon>
                  ) : (
                    <SvgIcon color="error">
                      <ClearIcon />
                    </SvgIcon>
                  )}
                  <Typography sx={{ fontWeight: 500 }} variant="body2">
                    {feature?.describe}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Card>
      </Box>
    </>
  );
};
